import { TypedDocumentNode, gql } from '@apollo/client';
import { faCardsBlank, faUser } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import styled from 'styled-components';

import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import {
  IOsAppFeature,
  NotIOsAppFeature,
} from '@sorare/core/src/components/BuyableFeature';
import FavoriteHeartButton from '@sorare/core/src/components/favorites/FavoriteHeartButton';
import Avatar from '@sorare/core/src/components/user/Avatar';
import UserName from '@sorare/core/src/components/user/UserName';
import {
  ANY_SPORT_PLAYERS_SLUG,
  ANY_SPORT_PLAYERS_SLUG_CARDS,
} from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useAmountWithConversion from '@sorare/core/src/hooks/useAmountWithConversion';
import { zeroMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { isType } from '@sorare/core/src/lib/gql';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { OwnerMyClubLink } from 'components/TokenPreview/TokenOwner';
import TokenTransferTypeIcon from 'components/token/TokenTransferTypeIcon';

import {
  RecommendationHeader_anyCard,
  RecommendationHeader_anyPlayer,
} from './__generated__/index.graphql';

const Root = styled(Horizontal).attrs({ gap: 1 })`
  justify-content: space-between;
  position: relative;
  max-width: 100%;
`;

const Actions = styled(Horizontal).attrs({ gap: 0 })`
  flex-shrink: 0;
`;

const WrapperOwnerMyClubLink = styled(BodyM)`
  padding: 0 var(--unit);
  display: flex;
  border-radius: var(--double-unit);
  background-color: var(--c-neutral-400);
`;

const Name = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font: var(--t-12);
  font-weight: var(--t-bold);
`;

const User = styled(Horizontal)`
  padding: var(--half-unit) 0;
`;

const ChipsRow = styled(Horizontal).attrs({ gap: 1 })`
  align-items: stretch;
  flex-wrap: wrap;
`;

const Infos = styled(Horizontal).attrs({ gap: 0.5 })`
  align-items: baseline;
`;

type Props = {
  subscribable: RecommendationHeader_anyPlayer | RecommendationHeader_anyCard;
  playerSlug: string;
  chip?: ReactNode;
  refetch: () => void;
  trackForYouClick: () => void;
};

export const Header = ({
  subscribable,
  playerSlug,
  chip,
  refetch,
  trackForYouClick,
}: Props) => {
  const { currentUserSubscription } = subscribable;
  const { generateSportContextPath } = useSportContext();

  const isCard =
    isType(subscribable, 'BaseballCard') ||
    isType(subscribable, 'Card') ||
    isType(subscribable, 'NBACard');

  const { main } = useAmountWithConversion({
    monetaryAmount:
      (isCard && subscribable?.tokenOwner?.amounts) || zeroMonetaryAmount,
  });

  const defaultChip =
    isCard && subscribable?.tokenOwner?.user ? (
      <ChipsRow gap={1}>
        <WrapperOwnerMyClubLink>
          <NotIOsAppFeature>
            <OwnerMyClubLink
              withAvatar
              sport={subscribable.sport}
              user={subscribable?.tokenOwner?.user}
              onClick={trackForYouClick}
            />
          </NotIOsAppFeature>
          <IOsAppFeature>
            <User center>
              <Avatar
                user={subscribable?.tokenOwner?.user}
                variant="extraSmall"
                rounded
              />
              <Name>
                <UserName user={subscribable?.tokenOwner?.user} />
              </Name>
            </User>
          </IOsAppFeature>
        </WrapperOwnerMyClubLink>
        {subscribable?.tokenOwner && (
          <Chip
            size="smaller"
            label={
              <Infos>
                <TokenTransferTypeIcon
                  color="var(--c-neutral-1000)"
                  transferType={subscribable?.tokenOwner?.transferType}
                />
                <LabelS bold color="var(--c-neutral-1000)">
                  {main}
                </LabelS>
              </Infos>
            }
            custom={{
              background: 'var(--c-neutral-400)',
            }}
          />
        )}
      </ChipsRow>
    ) : null;
  return (
    <Root>
      <div>{chip || defaultChip}</div>
      <Actions>
        <FavoriteHeartButton
          color="transparent"
          subscribable={subscribable}
          currentUserSubscription={currentUserSubscription}
          onToggled={() => {
            refetch();
            trackForYouClick();
          }}
        />
        <NotIOsAppFeature>
          <IconButton
            color="transparent"
            onClick={trackForYouClick}
            to={generateSportContextPath(
              isCard ? ANY_SPORT_PLAYERS_SLUG : ANY_SPORT_PLAYERS_SLUG_CARDS,
              {
                params: { slug: playerSlug },
              }
            )}
          >
            <FontAwesomeIcon
              icon={isCard ? faUser : faCardsBlank}
              color="var(--c-neutral-600)"
            />
          </IconButton>
        </NotIOsAppFeature>
      </Actions>
    </Root>
  );
};

Header.fragments = {
  anyCard: gql`
    fragment RecommendationHeader_anyCard on AnyCardInterface {
      slug
      sport
      tokenOwner {
        id
        transferType
        from
        amounts {
          ...MonetaryAmountFragment_monetaryAmount
        }
        user {
          slug
          ...OwnerMyClubLink_User
          ...Avatar_publicUserInfoInterface
          ...UserName_publicUserInfoInterface
        }
      }
      currentUserSubscription {
        slug
      }
    }
    ${OwnerMyClubLink.fragments.user}
    ${Avatar.fragments.publicUserInfoInterface}
    ${UserName.fragments.user}
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<RecommendationHeader_anyCard>,
  anyPlayer: gql`
    fragment RecommendationHeader_anyPlayer on AnyPlayerInterface {
      slug
      currentUserSubscription {
        slug
      }
    }
  ` as TypedDocumentNode<RecommendationHeader_anyPlayer>,
};
